// Servidor de la pantalla pública real de reserva (entorno de PRUEBA de Bókun).
// El navegador nunca envía precios: sólo fecha, horario, cantidades y datos de
// contacto. Los importes y la disponibilidad se releen en Bókun.
import { createServerFn } from "@tanstack/react-start";
import { getRequestHost } from "@tanstack/react-start/server";
import { z } from "zod";
import type { BookingScreenData } from "@/lib/bokun-test/public-booking.server";

const AnswerSchema = z.object({
  questionId: z.string().trim().min(1).max(80),
  values: z.array(z.string().trim().max(500)).min(1).max(10),
});

export const StartSchema = z.object({
  activityId: z
    .string()
    .trim()
    .regex(/^\d{1,12}$/),
  date: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/),
  startTimeId: z
    .string()
    .trim()
    .regex(/^\d{1,12}$/)
    .nullable()
    .optional()
    .default(null),
  rateId: z
    .string()
    .trim()
    .regex(/^\d{1,12}$/)
    .nullable()
    .optional()
    .default(null),
  items: z
    .array(
      z.object({
        pricingCategoryId: z
          .string()
          .trim()
          .regex(/^\d{1,12}$/),
        quantity: z.number().int().min(0).max(30),
      }),
    )
    .min(1)
    .max(10),
  contact: z.object({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    email: z.string().trim().email().max(255),
    phone: z.string().trim().max(30).optional().default(""),
    language: z.string().trim().min(2).max(5).optional().default("es"),
  }),
  answers: z.array(AnswerSchema).max(40).optional().default([]),
  passengerAnswers: z.array(AnswerSchema).max(40).optional().default([]),
  extras: z
    .array(
      z.object({
        extraId: z
          .string()
          .trim()
          .regex(/^\d{1,12}$/),
        quantity: z.number().int().min(0).max(30),
      }),
    )
    .max(20)
    .optional()
    .default([]),
  pickupPlaceId: z.string().trim().max(40).nullable().optional().default(null),
  dropoffPlaceId: z.string().trim().max(40).nullable().optional().default(null),
  pickupNote: z.string().trim().max(300).nullable().optional().default(null),
  dropoffNote: z.string().trim().max(300).nullable().optional().default(null),
  idempotencyKey: z.string().trim().regex(/^[A-Za-z0-9_-]{8,80}$/).nullable().optional().default(null),
  // Total aceptado por el cliente: sólo se compara, nunca se cobra.
  expectedAmount: z.number().nonnegative().max(10_000_000).nullable().optional().default(null),
  // Versión de la política de cancelación aceptada en el paso de pago.
  cancellationPolicyVersion: z.string().trim().max(60).nullable().optional().default(null),
  // Consentimiento explícito para UN recordatorio si no termina de pagar.
  recoveryConsent: z.boolean().optional().default(false),
  resumePath: z.string().trim().regex(/^\/[A-Za-z0-9/_\-]{1,200}$/).nullable().optional().default(null),
});

function originFromHost(): string {
  try {
    const host = getRequestHost();
    if (!host) return "http://localhost:8080";
    const proto = host.includes("localhost") ? "http" : "https";
    return `${proto}://${host}`;
  } catch {
    return "http://localhost:8080";
  }
}

export const getBokunTestBookingScreen = createServerFn({ method: "GET" })
  .inputValidator((input: { activityId: string; days?: number }) =>
    z
      .object({
        activityId: z
          .string()
          .trim()
          .regex(/^\d{1,12}$/),
        days: z.number().int().min(1).max(120).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<BookingScreenData | { error: string }> => {
    try {
      const { loadBookingScreen } = await import("@/lib/bokun-test/public-booking.server");
      return await loadBookingScreen({ activityId: data.activityId, days: data.days ?? 60 });
    } catch (e) {
      console.error("[bokun-test/screen]", (e as Error).message);
      return { error: "No pudimos cargar la disponibilidad. Vuelve a intentarlo en unos minutos." };
    }
  });

export const startBokunTestBooking = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => StartSchema.parse(input))
  .handler(async ({ data }) => {
    const { startPublicBooking } = await import("@/lib/bokun-test/public-booking.server");
    return await startPublicBooking({
      activityId: data.activityId,
      date: data.date,
      startTimeId: data.startTimeId,
      rateId: data.rateId,
      items: data.items,
      contact: data.contact,
      answers: data.answers,
      passengerAnswers: data.passengerAnswers,
      extras: data.extras,
      pickupPlaceId: data.pickupPlaceId,
      dropoffPlaceId: data.dropoffPlaceId,
      pickupNote: data.pickupNote,
      dropoffNote: data.dropoffNote,
      idempotencyKey: data.idempotencyKey,
      expectedAmount: data.expectedAmount,
      cancellationPolicyVersion: data.cancellationPolicyVersion,
      recoveryConsent: data.recoveryConsent,
      resumePath: data.resumePath,
      origin: originFromHost(),
    });
  });
